import { useCallback, useEffect, useState } from "react";
import { ArrowClockwise, XCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { unwrap } from "../../lib/helpers";
import { KVTable, DynamicTable } from "../../components/DataViews";
import { StatCard } from "../../components/StatCard";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";

function toRows(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((e) => (e && typeof e === "object" ? e : { message: e }));
}

export function HealthTab({ businessId, projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard/projects/health", {
        params: { wydely_business_id: businessId, wydely_project_id: projectId },
      });
      setData(unwrap(res.data));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [businessId, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return (
      <div className="space-y-3" data-testid="health-loading">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );

  const errors = toRows(data?.errors);
  const warnings = toRows(data?.warnings);
  const rest = { ...(data || {}) };
  delete rest.errors;
  delete rest.warnings;

  return (
    <div className="space-y-6" data-testid="health-tab">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} data-testid="health-refresh">
          <ArrowClockwise className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard label="Errors" value={errors.length} tone={errors.length ? "error" : "success"} testId="health-errors-count" />
        <StatCard label="Warnings" value={warnings.length} tone={warnings.length ? "warning" : "success"} testId="health-warnings-count" />
      </div>

      {errors.length > 0 && (
        <section className="space-y-3">
          <h3 className="flex items-center gap-2 text-base font-display font-bold tracking-tight text-red-600 dark:text-red-400">
            <XCircle className="h-5 w-5" weight="bold" /> Errors
          </h3>
          <DynamicTable rows={errors} testId="health-errors-table" />
        </section>
      )}

      {warnings.length > 0 && (
        <section className="space-y-3">
          <h3 className="flex items-center gap-2 text-base font-display font-bold tracking-tight text-amber-500">
            <Warning className="h-5 w-5" weight="bold" /> Warnings
          </h3>
          <DynamicTable rows={warnings} testId="health-warnings-table" />
        </section>
      )}

      <section className="space-y-3">
        <h3 className="text-base font-display font-bold tracking-tight">Connection & State</h3>
        <KVTable data={rest} testId="health-details-table" />
      </section>
    </div>
  );
}
